// csvImportUtils.js
// Logique pure d'import CSV : détection du séparateur, parsing, nettoyage des en-têtes.
// Aucune dépendance à React / antd : facilement testable en isolation.

// --- Libellés d'affichage pour les délimiteurs détectés ---
export const DELIM_LABELS = {
  ",": "virgule ( , )",
  ";": "point-virgule ( ; )",
  "\t": "tabulation",
  "|": "pipe ( | )",
};

export function toSnakeCase(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// --- Détection du séparateur en respectant les guillemets ---
export function detectDelimiter(text) {
  const candidates = [",", ";", "\t", "|"];
  const sampleLines = extractSampleLines(text, 5);

  let bestDelimiter = ",";
  let bestScore = -1;

  for (const delim of candidates) {
    const counts = sampleLines.map((line) => countOutsideQuotes(line, delim));
    if (counts.length === 0) continue;

    const allSame = counts.every((c) => c === counts[0]);
    const total = counts.reduce((a, b) => a + b, 0);
    const score = allSame && counts[0] > 0 ? counts[0] * 1000 : total;

    if (score > bestScore) {
      bestScore = score;
      bestDelimiter = delim;
    }
  }

  return bestDelimiter;
}

export function countOutsideQuotes(line, char) {
  let count = 0;
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (c === char && !inQuotes) {
      count++;
    }
  }
  return count;
}

export function extractSampleLines(text, n) {
  const lines = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length && lines.length < n; i++) {
    const char = text[i];

    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
    } else if (char === "\n" && !inQuotes) {
      lines.push(current);
      current = "";
    } else if (char !== "\r") {
      current += char;
    }
  }

  if (current !== "" && lines.length < n) {
    lines.push(current);
  }

  return lines;
}

// --- Parseur CSV robuste (gère guillemets, guillemets échappés, CRLF) ---
export function parseCSV(text, delimiter = ",") {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        row.push(field);
        field = "";
      } else if (char === "\r") {
        // ignoré
      } else if (char === "\n") {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += char;
      }
    }
  }

  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows.filter((r) => r.length > 1 || r[0] !== "");
}

// --- Nettoyage des en-têtes : trim + remplacement des vides ---
export function cleanHeaders(rawHeaders) {
  return rawHeaders.map((h, idx) => {
    const trimmed = (h ?? "").trim();
    return trimmed !== "" ? trimmed : `Colonne_${idx + 1}`;
  });
}

export function findDuplicates(headers) {
  const seen = new Map();
  const duplicates = new Set();

  headers.forEach((h) => {
    seen.set(h, (seen.get(h) || 0) + 1);
  });

  seen.forEach((count, name) => {
    if (count > 1) duplicates.add(name);
  });

  return Array.from(duplicates);
}

export function makeHeadersUnique(headers) {
  const counts = {};
  return headers.map((h) => {
    if (!counts[h]) {
      counts[h] = 1;
      return h;
    }
    counts[h] += 1;
    return `${h}_${counts[h]}`;
  });
}

/**
 * Fonction "haut niveau" qui prend le texte brut d'un fichier CSV
 * et retourne toutes les infos nécessaires à l'aperçu + à l'import.
 *
 * @param {string} text - contenu brut du fichier CSV
 * @param {string} fileName - nom du fichier (pour affichage)
 * @returns {{
 *   uniqueHeaders: string[],
 *   duplicates: string[],
 *   dataRows: string[][],
 *   delimiter: string,
 *   fileName: string
 * } | null} null si le CSV est vide
 */
export function buildCSVResult(text, fileName) {
  const detectedDelimiter = detectDelimiter(text);
  const rows = parseCSV(text, detectedDelimiter);

  if (rows.length === 0) {
    return null;
  }

  const rawHeaders = rows[0];
  const cleanedHeaders = cleanHeaders(rawHeaders);
  const duplicates = findDuplicates(cleanedHeaders);
  const uniqueHeaders = makeHeadersUnique(cleanedHeaders);
  const dataRows = rows.slice(1);

  return {
    uniqueHeaders,
    duplicates,
    dataRows,
    delimiter: detectedDelimiter,
    fileName,
  };
}
